import { type ComponentType, createElement } from 'react';

import { cn } from '@/lib/utils';

type SvgComponent = ComponentType<React.SVGProps<SVGSVGElement>>;

type SvgIconProps = {
  // Turbopack+SVGR → component; webpack static import → { src }; plain URL → string
  src: string | SvgComponent | { src: string; default?: SvgComponent };
  alt?: string;
  /** Use for multi-color SVGs (logos, badges, illustrations) */
  asImg?: boolean;
} & React.HTMLAttributes<HTMLElement>;

function resolveSrc(src: SvgIconProps['src']): string | SvgComponent {
  if (typeof src === 'string' || typeof src === 'function') return src;

  // SVGR / webpack interop: module namespace or StaticImageData
  if (src && typeof src === 'object') {
    if ('default' in src && typeof src.default === 'function') return src.default;
    if ('src' in src && typeof src.src === 'string') return src.src;
  }

  return src as unknown as SvgComponent;
}

export function SvgIcon({
  src,
  className,
  alt = '',
  asImg,
  style,
  ...props
}: SvgIconProps) {
  const resolved = resolveSrc(src);

  if (typeof resolved === 'string') {
    if (asImg) {
      return (
        <img
          src={resolved}
          alt={alt}
          className={className}
          style={style}
          {...(props as React.ImgHTMLAttributes<HTMLImageElement>)}
        />
      );
    }

    return (
      <span
        role='img'
        aria-label={alt}
        className={cn('inline-block', className)}
        style={{
          maskImage: `url(${resolved})`,
          WebkitMaskImage: `url(${resolved})`,
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
          backgroundColor: 'currentColor',
          ...style,
        }}
        {...props}
      />
    );
  }

  return createElement(resolved, {
    className: asImg ? className : cn('inline-block', className),
    style,
    'aria-label': alt,
    ...props,
  } as React.SVGProps<SVGSVGElement>);
}
